import React, { useState } from 'react';
import { Plus, Edit, Eye, EyeOff, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { createNotice, updateNotice, deleteNotice, toggleNotice } from '../../utils/api';

export const AdminNotices = ({ notices, loadData }) => {
  const [editingNotice, setEditingNotice] = useState(null);
  const [noticeForm, setNoticeForm] = useState({
    title: '', content: '', link: '', link_text: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    is_active: true
  });

  const handleSave = async () => {
    try {
      const data = editingNotice || noticeForm;
      if (!data.title || !data.content || !data.start_date || !data.end_date) { toast.error('Please fill in required fields'); return; }
      if (editingNotice) { await updateNotice(editingNotice.id, data); toast.success('Notice updated!'); setEditingNotice(null); }
      else { await createNotice(data); toast.success('Notice created!'); setNoticeForm({ title: '', content: '', link: '', link_text: '', start_date: new Date().toISOString().split('T')[0], end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], is_active: true }); }
      loadData();
    } catch (err) { toast.error('Failed to save notice'); }
  };

  const current = editingNotice || noticeForm;
  const setCurrent = (val) => editingNotice ? setEditingNotice(val) : setNoticeForm(val);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="notices-tab-content">
      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">{editingNotice ? 'Edit Notice' : 'Create Notice'}</h2>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={current.title} onChange={(e) => setCurrent({...current, title: e.target.value})} placeholder="e.g., New Video Launch!" className="mt-2" /></div>
          <div><Label>Content</Label><Textarea value={current.content} onChange={(e) => setCurrent({...current, content: e.target.value})} placeholder="Brief notice message..." className="mt-2" rows={2} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Link URL (optional)</Label><Input value={current.link} onChange={(e) => setCurrent({...current, link: e.target.value})} placeholder="https://..." className="mt-2" /></div>
            <div><Label>Link Text</Label><Input value={current.link_text} onChange={(e) => setCurrent({...current, link_text: e.target.value})} placeholder="Watch Now" className="mt-2" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Start Date</Label><Input type="date" value={current.start_date} onChange={(e) => setCurrent({...current, start_date: e.target.value})} className="mt-2" /></div>
            <div><Label>End Date</Label><Input type="date" value={current.end_date} onChange={(e) => setCurrent({...current, end_date: e.target.value})} className="mt-2" /></div>
          </div>
          <div className="flex items-center gap-2"><input type="checkbox" checked={current.is_active} onChange={(e) => setCurrent({...current, is_active: e.target.checked})} className="h-4 w-4" /><Label>Active</Label></div>
          <div className="flex gap-3 pt-4">
            <Button className="flex-1 bg-[#8B1818] hover:bg-[#701313] text-white" onClick={handleSave}><Plus className="mr-2 h-4 w-4" />{editingNotice ? 'Update Notice' : 'Create Notice'}</Button>
            {editingNotice && <Button variant="outline" onClick={() => setEditingNotice(null)}>Cancel</Button>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">All Notices</h2>
        {notices.length === 0 ? <p className="text-slate-500 text-center py-8">No notices yet.</p> : (
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {notices.map((notice) => {
              const today = new Date().toISOString().split('T')[0];
              const isLive = notice.is_active && notice.start_date <= today && notice.end_date >= today;
              return (
                <div key={notice.id} className={`border rounded-lg p-4 ${isLive ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{notice.title}</h3>
                        {isLive && <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">LIVE</span>}
                        {!notice.is_active && <span className="px-2 py-0.5 bg-slate-400 text-white text-xs rounded-full">Inactive</span>}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{notice.content}</p>
                      {notice.link && <a href={notice.link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#8B1818] hover:underline mt-1 block">{notice.link_text || notice.link}</a>}
                      <p className="text-xs text-slate-500 mt-2">{notice.start_date} &rarr; {notice.end_date}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingNotice(notice)}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant={notice.is_active ? "outline" : "default"} className={notice.is_active ? "" : "bg-green-600 hover:bg-green-700 text-white"} onClick={async () => { try { await toggleNotice(notice.id); toast.success(notice.is_active ? 'Deactivated' : 'Activated'); loadData(); } catch (err) { toast.error('Failed'); } }}>{notice.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                      <Button size="sm" variant="destructive" onClick={async () => { if (window.confirm('Delete?')) { try { await deleteNotice(notice.id); toast.success('Deleted'); loadData(); } catch (err) { toast.error('Failed'); } } }}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
